import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../utils/tableVariables';
import { WorkflowProgressValue } from '../../utils/workflow';

type TRIPState = {
  // Pagination
  page: number;
  pageSize: number;

  // Search/Filter
  search: string;
  workflowFilter: WorkflowProgressValue | 'all';

  // Delete Dialog
  isDeleteDialogOpen: boolean;
  ripToDelete: { id: string; ripNumber: string } | null;

  // Snackbar notifications
  snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  };
};

const initialState: TRIPState = {
  page: 0,
  pageSize: DEFAULT_TABLE_PAGE_SIZE,
  search: '',
  workflowFilter: 'all',
  isDeleteDialogOpen: false,
  ripToDelete: null,
  snackbar: {
    open: false,
    message: '',
    severity: 'success',
  },
};

const ripSlice = createSlice({
  name: 'rip',
  initialState,
  reducers: {
    // Pagination
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },

    // Search - reset page when search changes
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.page = 0;
    },

    // Workflow filter - reset page when filter changes
    setWorkflowFilter: (state, action: PayloadAction<WorkflowProgressValue | 'all'>) => {
      state.workflowFilter = action.payload;
      state.page = 0;
    },

    // Delete dialog
    openDeleteDialog: (state, action: PayloadAction<{ id: string; ripNumber: string }>) => {
      state.isDeleteDialogOpen = true;
      state.ripToDelete = action.payload;
    },
    closeDeleteDialog: (state) => {
      state.isDeleteDialogOpen = false;
      state.ripToDelete = null;
    },

    // Snackbar
    showSnackbar: (state, action: PayloadAction<{ message: string; severity: 'success' | 'error' | 'warning' | 'info' }>) => {
      state.snackbar = {
        open: true,
        message: action.payload.message,
        severity: action.payload.severity,
      };
    },
    hideSnackbar: (state) => {
      state.snackbar.open = false;
    },

    // Reset all state
    resetRIPState: () => initialState,
  },
});

export const {
  setPage,
  setPageSize,
  setSearch,
  setWorkflowFilter,
  openDeleteDialog,
  closeDeleteDialog,
  showSnackbar,
  hideSnackbar,
  resetRIPState,
} = ripSlice.actions;

export default ripSlice.reducer;
